import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { externalSystemService, type ExternalSystemDetailDto, type ExternalApiMethodDto } from '../services/externalSystemService';
import { useNotificationStore } from '../store/notificationStore';
import { ApiMethodForm } from '../components/externalSystems/ApiMethodForm';
import { ApiMethodTester } from '../components/externalSystems/ApiMethodTester';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import EmptyState from '../components/data-display/EmptyState';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';

/**
 * External system detail page component.
 */
export const ExternalSystemDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [system, setSystem] = useState<ExternalSystemDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingMethodId, setEditingMethodId] = useState<string | null>(null);
  const [testingMethodId, setTestingMethodId] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSystem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await externalSystemService.getExternalSystem(id);
      setSystem(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load external system');
      addNotification('Failed to load external system', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this external system? This will also delete all associated API methods.')) {
      return;
    }

    try {
      await externalSystemService.deleteExternalSystem(id);
      addNotification('External system deleted successfully', 'success');
      navigate('/external-systems');
    } catch (err: any) {
      addNotification(err.message || 'Failed to delete external system', 'error');
    }
  };

  const handleDeleteMethod = async (methodId: string) => {
    if (!window.confirm('Are you sure you want to delete this API method?')) {
      return;
    }

    // Note: We don't have a delete API method endpoint yet, so this is a placeholder
    addNotification(`Delete API method (${methodId}) functionality coming soon`, 'info');
  };

  // Define table columns for API methods
  const apiMethodColumns: DataTableColumn<ExternalApiMethodDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (method) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {method.name}
          </Typography>
          {method.description && (
            <Typography variant="caption" color="text.secondary">
              {method.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'httpMethod',
      label: 'HTTP Method',
      minWidth: 120,
      render: (method) => <Chip label={method.httpMethod} size="small" variant="outlined" />,
    },
    {
      id: 'endpointPath',
      label: 'Endpoint',
      minWidth: 200,
      render: (method) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {method.endpointPath}
        </Typography>
      ),
    },
    {
      id: 'toolName',
      label: 'Tool Name',
      minWidth: 150,
    },
    {
      id: 'riskLevel',
      label: 'Risk Level',
      minWidth: 120,
      render: (method) => (
        <Chip
          label={method.riskLevel}
          size="small"
          color={
            method.riskLevel === 'Critical'
              ? 'error'
              : method.riskLevel === 'High'
              ? 'warning'
              : method.riskLevel === 'Medium'
              ? 'info'
              : 'default'
          }
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (method) => (
        <Chip
          label={method.isActive ? 'Active' : 'Inactive'}
          color={method.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 150,
      align: 'right',
      render: (method) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="Test">
            <IconButton
              size="small"
              onClick={() => setTestingMethodId(method.id)}
            >
              <PlayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => setEditingMethodId(method.id)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDeleteMethod(method.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <PageDetailLayout
      title={system?.name || 'External System'}
      subtitle={system?.description}
      breadcrumbs={[{ label: 'External Systems', href: '/external-systems' }, { label: system?.name || 'External System' }]}
      backTo={{ label: 'External Systems', href: '/external-systems' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      status={system ? { label: system.isActive ? 'Active' : 'Inactive', color: system.isActive ? 'success' : 'default' } : undefined}
      chips={system ? [
        { label: `Base URL: ${system.baseUrl}`, variant: 'outlined' as const },
        { label: `Auth: ${system.authenticationType}`, variant: 'outlined' as const },
        { label: `${system.apiMethodCount} API Methods` },
      ] : []}
      actions={system ? [
        { icon: <EditIcon />, label: 'Edit', onClick: () => navigate(`/external-systems/${id}/edit`), variant: 'outlined' as const },
        { icon: <DeleteIcon />, label: 'Delete', onClick: handleDelete, color: 'error' as const, variant: 'outlined' as const },
      ] : []}
      tabs={system ? [
        {
          label: 'API Methods',
          value: 'api-methods',
          content: (
            <Box>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setEditingMethodId('new')}
                >
                  Add API Method
                </Button>
              </Box>
              <DataTable
                columns={apiMethodColumns}
                rows={system.apiMethods}
                loading={false}
                emptyState={
                  <EmptyState
                    title="No API methods defined"
                    description="Add an API method to get started."
                    actionLabel="Add API Method"
                    onAction={() => setEditingMethodId('new')}
                  />
                }
              />
            </Box>
          ),
        },
        {
          label: editingMethodId && editingMethodId !== 'new' ? 'Edit Method' : 'Add Method',
          value: 'edit-method',
          content: (
            <ApiMethodForm
              systemId={id!}
              methodId={editingMethodId === 'new' ? undefined : editingMethodId || undefined}
              onSuccess={() => {
                setEditingMethodId(null);
                loadSystem();
              }}
            />
          ),
        },
        ...(testingMethodId ? [{
          label: 'Test Method',
          value: 'test-method',
          content: (
            <>
              <ApiMethodTester systemId={id!} methodId={testingMethodId} />
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => setTestingMethodId(null)}
                >
                  Back to Methods
                </Button>
              </Box>
            </>
          ),
        }] : []),
      ] : []}
    />
  );
};
